import LandingPageSkeleton from 'components/Landing/LandingPageSkeleton';
import useMobxStores from 'hooks/useMobxStores';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from 'components/Landing/LandingLayout';
import { Helmet } from 'react-helmet';
import { Breadcrumb, BreadcrumbItem, Flex, Tab } from '@patternfly/react-core';
import { CatalogEntry } from 'types';
import BaseOfferingCard from 'components/OfferingCards/BaseOfferingCard';
import { observer } from 'mobx-react';
import useTabs from 'hooks/useTabs';
import { SKILLS_ASSESSMENTS_DASHBOARD_URL } from 'config/constants';

enum SkillsAssessmentTab {
  Suggestions = 'suggestions',
}

enum SkillsAssessmentSuggestionAction {
  ViewCourse = 'View course',
}

const SkillsAssessments = () => {
  const { t } = useTranslation();
  const { routerStore, catalogStore } = useMobxStores();

  const { route } = routerStore;
  const { id } = (route as any).params;

  const { activeTabKey, handleTabClick } = useTabs<SkillsAssessmentTab>({
    route: route as { params?: Record<string, string> },
    defaultTab: SkillsAssessmentTab.Suggestions,
    isValidTab: (v): v is SkillsAssessmentTab =>
      Object.values(SkillsAssessmentTab).includes(v as SkillsAssessmentTab),
    onNavigate: (tab) =>
      routerStore.navigate('skills-assessments', { id, tab }),
  });

  const skillsAssessment = catalogStore.getSkillsAssessmentById(id);
  const title = skillsAssessment?.metadata.assessment.name;
  const suggestions = useMemo(
    () =>
      skillsAssessment?.suggestions.reduce((acc, suggestion) => {
        if (suggestion.action === SkillsAssessmentSuggestionAction.ViewCourse) {
          const entry =
            catalogStore.allUniqueCatalogEntries[
              suggestion.label.toLowerCase()
            ];

          if (entry) return [...acc, entry];
        }

        return acc;
      }, [] as CatalogEntry[]),
    [skillsAssessment?.suggestions, catalogStore.uniqueCatalogEntries],
  );

  const score = useMemo((): number => {
    const scores = skillsAssessment?.score || [];

    if (!scores.length) return 0;

    const totalPoints = scores.reduce((sum, s) => sum + s.points, 0);
    const totalMaximum = scores.reduce((sum, s) => sum + s.maximum, 0);

    if (totalMaximum === 0) return 0;

    return Math.round((totalPoints / totalMaximum) * 100);
  }, [skillsAssessment?.score]);

  return (
    <LandingLayout>
      {!skillsAssessment ? (
        <LandingPageSkeleton breadcrumbParentLink={t('Skills Assessments')} />
      ) : (
        <>
          <Helmet
            title={`${skillsAssessment?.metadata.assessment.name} | Skills Assessment`}
          />

          <LandingLayout.Breadcrumb>
            <Breadcrumb>
              <BreadcrumbItem>{t('Skills Assessments')}</BreadcrumbItem>
              <BreadcrumbItem isActive>{title}</BreadcrumbItem>
            </Breadcrumb>
          </LandingLayout.Breadcrumb>

          <LandingLayout.Header title={title} />

          <LandingLayout.Content>
            <LandingLayout.Main>
              <LandingLayout.Tabs
                mountOnEnter
                isBox
                activeKey={activeTabKey}
                onSelect={handleTabClick}
              >
                <Tab
                  eventKey={SkillsAssessmentTab.Suggestions}
                  title={t('Suggestions')}
                  aria-label={t('Skills assessment suggestions')}
                  data-analytics-id="skills-assessments-suggestions-tab"
                >
                  <LandingLayout.Progress>
                    {t('Score: {{formattedProgress}}%', {
                      formattedProgress: score,
                    })}
                  </LandingLayout.Progress>

                  <Flex gap={{ default: 'gapMd' }}>
                    {suggestions?.map((suggestion) => (
                      <BaseOfferingCard
                        key={suggestion?.code}
                        entry={suggestion}
                        coursesShowLiveClassInfo
                      />
                    ))}
                  </Flex>
                </Tab>
              </LandingLayout.Tabs>
            </LandingLayout.Main>

            <LandingLayout.Sidebar>
              <LandingLayout.SidebarButton
                component="a"
                isBlock
                title={t('Go to your dashboard')}
                data-analytics-id="skills-assessments-go-to-dashboard-btn"
                rel="noopener noreferrer"
                target="_blank"
                href={SKILLS_ASSESSMENTS_DASHBOARD_URL}
              >
                {t('Go to your dashboard')}
              </LandingLayout.SidebarButton>
            </LandingLayout.Sidebar>
          </LandingLayout.Content>
        </>
      )}
    </LandingLayout>
  );
};

export default observer(SkillsAssessments);
